// Camera operating settings, separate from exposure and visible-light criteria.
const number = (v, lo, hi) => v !== '' && v != null && Number.isFinite(+v) ? Math.max(lo, Math.min(hi, +v)) : null;
const choice = (v, values, fallback) => values.includes(v) ? v : fallback;
const text = v => String(v ?? '').trim().slice(0, 1000);
export const NIGHT_DEFAULTS = Object.freeze({ nightLux: 5, dayLux: 10, delaySeconds: 5 });

export function normalizeCameraNightSettings(raw = {}) {
  raw ||= {};
  const nightLux = number(raw.nightLux, .001, 10000), day = number(raw.dayLux, .002, 20000);
  return {
    mode: choice(raw.mode, ['auto', 'day', 'night'], 'auto'),
    capability: choice(raw.capability, ['auto', 'day-night', 'colour-only'], 'auto'),
    nightLux, dayLux: day == null ? null : Math.max(day, (nightLux ?? 0) + .001),
    delaySeconds: number(raw.delaySeconds, 0, 120),
    thresholdBasis: choice(raw.thresholdBasis, ['assumed', 'measured', 'manufacturer'], 'assumed'), thresholdSource: text(raw.thresholdSource),
    ir: choice(raw.ir, ['auto', 'off'], 'auto'), irRange: number(raw.irRange, 0, 1000), irBeam: number(raw.irBeam, 5, 170), irStrength: number(raw.irStrength, .1, 4) ?? 1
  };
}

// Optional manufacturer-library metadata. Missing lux values remain unknown;
// a minimum-illumination specification is never used as a switching threshold.
export function normalizeNightVisionSpec(raw = {}) {
  const source = /^https?:\/\//i.test(text(raw.source)) ? text(raw.source) : '';
  const nightLux = number(raw.nightLux, .001, 10000), dayLux = number(raw.dayLux, .002, 20000);
  const thresholdsKnown = source && nightLux != null && dayLux > nightLux;
  return { capability: choice(raw.capability, ['day-night', 'colour-only'], 'unknown'), source,
    nightLux: thresholdsKnown ? nightLux : null, dayLux: thresholdsKnown ? dayLux : null,
    delaySeconds: source ? number(raw.delaySeconds, 0, 120) : null };
}

export function cameraNightSettings(camera, model) {
  const saved = normalizeCameraNightSettings(camera?.nightVision), spec = normalizeNightVisionSpec(model?.details?.nightVision);
  const capability = saved.capability !== 'auto' ? saved.capability : spec.capability !== 'unknown' ? spec.capability : model?.ir > 0 ? 'day-night' : 'unknown';
  const override = saved.nightLux != null || saved.dayLux != null;
  const nightLux = override ? saved.nightLux ?? NIGHT_DEFAULTS.nightLux : spec.nightLux ?? NIGHT_DEFAULTS.nightLux;
  const dayLux = Math.max(nightLux + .001, override ? saved.dayLux ?? Math.max(NIGHT_DEFAULTS.dayLux, nightLux * 2) : spec.dayLux ?? NIGHT_DEFAULTS.dayLux);
  const basis = override ? saved.thresholdSource ? saved.thresholdBasis : 'assumed' : spec.nightLux != null ? 'manufacturer' : 'assumed';
  return { ...saved, capability, nightLux, dayLux, delaySeconds: saved.delaySeconds ?? spec.delaySeconds ?? NIGHT_DEFAULTS.delaySeconds,
    thresholdBasis: basis, thresholdSource: override ? saved.thresholdSource : basis === 'manufacturer' ? spec.source : '',
    capabilityBasis: saved.capability !== 'auto' ? 'User setting' : spec.capability !== 'unknown' ? 'Library specification' : model?.ir > 0 ? 'Inferred from library IR range' : 'Not specified',
    irRange: saved.irRange ?? Math.max(0, model?.ir || 0), irRangeBasis: saved.irRange != null ? 'User assumption' : model?.ir > 0 ? 'Library range' : 'Not specified' };
}

export function advanceNightState(previous, settings, meter, now) {
  const mode = settings.capability === 'day-night' ? settings.mode : 'day';
  if (mode !== 'auto') return { mode, pending: null, since: null, uncertain: settings.capability === 'unknown' };
  const valid = meter && Number.isFinite(meter.low) && Number.isFinite(meter.high);
  const current = previous?.mode || (valid && meter.high < settings.nightLux ? 'night' : 'day');
  const desired = valid && current === 'day' && meter.high < settings.nightLux ? 'night'
    : valid && current === 'night' && meter.low > settings.dayLux ? 'day' : current;
  const uncertain = !valid || (current === 'day' ? meter.low < settings.nightLux && meter.high >= settings.nightLux : meter.low <= settings.dayLux && meter.high > settings.dayLux);
  if (desired === current) return { mode: current, pending: null, since: null, uncertain };
  const since = previous?.pending === desired ? previous.since : now;
  if (now - since >= settings.delaySeconds * 1000) return { mode: desired, pending: null, since: null, uncertain: false };
  return { mode: current, pending: desired, since, uncertain: false };
}
